import React from 'react';
import { ChevronRight } from 'lucide-react';

import { EXPOSURE_BADGES, EXPOSURE_STATE_TITLE, exposureBadgeState } from './attribute-exposure';
import type { AttributeRowView } from './schema-editor.types';
import styles from './schema-editor.module.css';

export interface AttributeRowProps {
  attribute: AttributeRowView;
  selected: boolean;
  showAgentTools: boolean;
  onSelect: (name: string) => void;
}

/**
 * One attribute in the attributes table. The whole row is the click target that
 * opens the editor panel, so it renders as a button rather than a table row.
 */
export const AttributeRow: React.FC<AttributeRowProps> = ({
  attribute,
  selected,
  showAgentTools,
  onSelect,
}) => {
  const hasMapping = attribute.mappingLabels.length > 0;
  const surfaces = EXPOSURE_BADGES.filter(
    surface => surface.key !== 'agentTools' || showAgentTools || attribute.exposure.agentTools,
  );

  return (
    <button
      type="button"
      onClick={() => onSelect(attribute.name)}
      data-selected={selected}
      data-dirty={attribute.dirty}
      className={styles.attributeRow}>
      <span className={styles.attributeCell}>
        <span className={styles.attributeNameLine}>
          <span className={styles.attributeName}>{attribute.name}</span>
          {attribute.required && (
            <span
              className={styles.attributeRequiredMark}
              title="Required on every record"
              aria-label="Required">
              *
            </span>
          )}
          {attribute.dirty && (
            <span
              className={styles.attributeDirtyBadge}
              title="This attribute has changes you have not saved">
              Unsaved
            </span>
          )}
        </span>
        <span className={styles.attributeDescription}>{attribute.description}</span>
      </span>

      <span className={styles.attributeTypeCell}>
        <span className={styles.attributeTypeChip}>{attribute.typeLabel}</span>
        {attribute.typeMeta && <span className={styles.attributeTypeMeta}>{attribute.typeMeta}</span>}
      </span>

      <span className={styles.attributeMappingCell}>
        {hasMapping ? (
          attribute.mappingLabels.map(label => (
            <span key={label} className={styles.attributeMappingPill}>
              {label}
            </span>
          ))
        ) : (
          <span className={styles.attributeNoMapping}>unmapped</span>
        )}
      </span>

      <span className={styles.attributeExposureCell}>
        {surfaces.map(({ key, label, Icon }) => {
          const state = exposureBadgeState(attribute.exposure[key], attribute.exposureNamed);
          const badgeTitle = `${label} — ${EXPOSURE_STATE_TITLE[state]}`;
          return (
            <span
              key={key}
              className={styles.attributeExposureBadge}
              data-state={state}
              title={badgeTitle}>
              <Icon size={14} />
            </span>
          );
        })}
      </span>

      <span className={styles.attributeChevronCell} aria-hidden="true">
        <ChevronRight size={16} />
      </span>
    </button>
  );
};
